import React, { useState } from 'react';
import { isAxiosError } from 'axios';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { FiDownload } from 'react-icons/fi';
import SaktiBccApiService from '../services/saktiBccApiService';
import { SaktiBccImageDownloadRequest, SaktiBccPercentRange, SaktiFilterOptions } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';
import extractApiError from '../utils/apiError';
import { getTodayInJakartaForApi } from '../utils/date';
import { trackDownload } from '../../activity';

// Mirrors Sakti Go bcc.MaxImageDownload / MaxImageRangeDays.
const MAX_IMAGES = 2000;
const MAX_RANGE_DAYS = 31;
const DAY_MS = 86_400_000;

const DEFAULT_CUT_POINTS = [5, 10, 20, 30];
const DEFAULT_PERCENTAGES: SaktiBccPercentRange[] = [
  { min: 5, max: 10 },
  { min: 10, max: 15 },
  { min: 25, max: 30 },
  { min: 25, max: 30 },
  { min: 25, max: 35 },
];

interface SaktiBccImageDownloadFormProps {
  options: SaktiFilterOptions;
  isDisabled?: boolean;
}

interface NumberCellProps {
  value: number;
  onChange: (value: number) => void;
  label: string;
  min?: number;
  max?: number;
}

const NumberCell: React.FC<NumberCellProps> = ({ value, onChange, label, min = 0, max }) => (
  <NumberInput
    size="sm"
    maxW="90px"
    min={min}
    max={max}
    value={value}
    onChange={(_, valueAsNumber) => onChange(Number.isNaN(valueAsNumber) ? 0 : valueAsNumber)}
  >
    <NumberInputField borderRadius="lg" aria-label={label} />
  </NumberInput>
);

// responseType 'blob' also turns JSON error bodies into Blobs.
const readDownloadError = async (err: unknown): Promise<string> => {
  if (isAxiosError(err) && err.response?.data instanceof Blob) {
    try {
      const body = JSON.parse(await err.response.data.text());
      return body.detail ?? body.error ?? body.message ?? err.message;
    } catch {
      return err.message;
    }
  }
  return extractApiError(err).message;
};

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const SaktiBccImageDownloadForm: React.FC<SaktiBccImageDownloadFormProps> = ({ options, isDisabled = false }) => {
  const toast = useToast();
  const [form, setForm] = useState<SaktiBccImageDownloadRequest>(() => ({
    date_start: getTodayInJakartaForApi(),
    date_end: getTodayInJakartaForApi(),
    estate_id: 0,
    total: 500,
    cut_points: DEFAULT_CUT_POINTS,
    percentages: DEFAULT_PERCENTAGES,
  }));
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (changes: Partial<SaktiBccImageDownloadRequest>) => setForm((prev) => ({ ...prev, ...changes }));
  const updateCutPoint = (index: number, value: number) =>
    update({ cut_points: form.cut_points.map((c, i) => (i === index ? value : c)) });
  const updatePercent = (index: number, key: keyof SaktiBccPercentRange, value: number) =>
    update({ percentages: form.percentages.map((p, i) => (i === index ? { ...p, [key]: value } : p)) });

  const sumMin = form.percentages.reduce((sum, p) => sum + p.min, 0);
  const sumMax = form.percentages.reduce((sum, p) => sum + p.max, 0);
  const rangeDays = (Date.parse(form.date_end) - Date.parse(form.date_start)) / DAY_MS;
  const isRangeValid = rangeDays >= 0 && rangeDays < MAX_RANGE_DAYS;
  const isCutPointsValid = form.cut_points.every((c, i) => c >= 1 && (i === 0 || c > form.cut_points[i - 1]));
  const isPercentValid =
    form.percentages.every((p) => p.min >= 0 && p.max <= 100 && p.min <= p.max) && sumMin <= 100 && sumMax >= 100;
  const canSubmit =
    form.estate_id > 0 && isRangeValid && form.total >= 1 && form.total <= MAX_IMAGES && isCutPointsValid && isPercentValid;

  const handleDownload = async () => {
    setIsDownloading(true);
    setError(null);
    try {
      const blob = await SaktiBccApiService.downloadImages(form);
      saveBlob(blob, `bcc-images_${form.estate_id}_${form.date_start}_${form.date_end}.zip`);
      trackDownload('Foto BCC', { estate_id: form.estate_id, date_start: form.date_start, date_end: form.date_end });
      toast({
        status: 'success',
        title: 'ZIP gambar BCC terunduh',
        description: 'Komposisi aktual per kelompok janjang ada di summary.csv.',
      });
    } catch (err) {
      setError(await readDownloadError(err));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box
      bg="white"
      borderRadius={SAKTI_UI_SIZE.cardRadius}
      p={SAKTI_UI_SIZE.cardPadding}
      borderWidth="1px"
      borderColor="gray.200"
    >
      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
        gap={SAKTI_UI_SIZE.filterGridGap}
        mb={5}
      >
        <FormControl isRequired>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>ESTATE</FormLabel>
          <Select
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            placeholder="Pilih estate"
            value={form.estate_id || ''}
            onChange={(event) => update({ estate_id: Number(event.target.value) || 0 })}
          >
            {options.estates.map((estate) => (
              <option key={estate.id} value={estate.odoo_id}>{estate.name}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl isRequired isInvalid={!isRangeValid}>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>TANGGAL MULAI</FormLabel>
          <Input
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            type="date"
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            value={form.date_start}
            max={form.date_end || undefined}
            onChange={(event) => update({ date_start: event.target.value })}
          />
        </FormControl>
        <FormControl isRequired isInvalid={!isRangeValid}>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            TANGGAL AKHIR (MAKS {MAX_RANGE_DAYS} HARI)
          </FormLabel>
          <Input
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            type="date"
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            value={form.date_end}
            min={form.date_start || undefined}
            onChange={(event) => update({ date_end: event.target.value })}
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            JUMLAH GAMBAR (MAKS {MAX_IMAGES})
          </FormLabel>
          <NumberInput
            size="sm"
            min={1}
            max={MAX_IMAGES}
            value={form.total}
            onChange={(_, value) => update({ total: Number.isNaN(value) ? 0 : value })}
          >
            <NumberInputField h={SAKTI_UI_SIZE.controlHeight} borderRadius={SAKTI_UI_SIZE.cardRadius} />
          </NumberInput>
        </FormControl>
      </Grid>

      <Text fontSize="sm" fontWeight="semibold" color="gray.800">Komposisi jumlah janjang per foto</Text>
      <Text fontSize="xs" color="gray.500" mb={2}>
        Foto diambil acak per kelompok. Kelompok yang stoknya kurang diisi kelompok lain sampai batas maks %.
      </Text>
      <Box
        overflowX="auto"
        maxW="560px"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius={SAKTI_UI_SIZE.cardRadius}
      >
        <Table size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Janjang</Th>
              <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Min %</Th>
              <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Maks %</Th>
            </Tr>
          </Thead>
          <Tbody>
            {form.percentages.map((pct, index) => (
              <Tr key={index}>
                <Td>
                  <HStack spacing={2} fontSize={SAKTI_UI_SIZE.tableCellFont}>
                    {index === 0 ? (
                      <Text w="90px">0</Text>
                    ) : (
                      <NumberCell
                        label={`Batas bawah kelompok ${index + 1}`}
                        min={1}
                        value={form.cut_points[index - 1]}
                        onChange={(value) => updateCutPoint(index - 1, value)}
                      />
                    )}
                    <Text color="gray.600" whiteSpace="nowrap">
                      {index === form.cut_points.length ? 'ke atas' : `s/d ${form.cut_points[index] - 1}`}
                    </Text>
                  </HStack>
                </Td>
                <Td>
                  <NumberCell
                    label={`Min persen kelompok ${index + 1}`}
                    max={100}
                    value={pct.min}
                    onChange={(value) => updatePercent(index, 'min', value)}
                  />
                </Td>
                <Td>
                  <NumberCell
                    label={`Maks persen kelompok ${index + 1}`}
                    max={100}
                    value={pct.max}
                    onChange={(value) => updatePercent(index, 'max', value)}
                  />
                </Td>
              </Tr>
            ))}
            <Tr bg="gray.50">
              <Td fontWeight="semibold" fontSize={SAKTI_UI_SIZE.tableCellFont}>Total</Td>
              <Td fontWeight="semibold" color={sumMin > 100 ? 'red.500' : 'gray.800'}>{sumMin}%</Td>
              <Td fontWeight="semibold" color={sumMax < 100 ? 'red.500' : 'gray.800'}>{sumMax}%</Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>

      {!isCutPointsValid && (
        <Alert status="warning" borderRadius="lg" mt={3} fontSize="sm">
          <AlertIcon />Batas bawah janjang harus naik berurutan dan minimal 1.
        </Alert>
      )}
      {!isPercentValid && (
        <Alert status="warning" borderRadius="lg" mt={3} fontSize="sm">
          <AlertIcon />Setiap min % ≤ maks %, total min % ≤ 100, dan total maks % ≥ 100.
        </Alert>
      )}
      {error && (
        <Alert status="error" borderRadius="lg" mt={3} fontSize="sm">
          <AlertIcon />{error}
        </Alert>
      )}

      <HStack justify="flex-end" spacing={2} mt={5}>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => update({ cut_points: DEFAULT_CUT_POINTS, percentages: DEFAULT_PERCENTAGES })}
          isDisabled={isDownloading}
        >
          Reset Aturan
        </Button>
        <Button
          size="sm"
          h={SAKTI_UI_SIZE.controlHeight}
          px={5}
          leftIcon={<FiDownload />}
          bg="customGreen.500"
          _hover={{ bg: 'customGreen.600' }}
          color="white"
          isLoading={isDownloading}
          loadingText="Menyiapkan ZIP"
          isDisabled={isDisabled || !canSubmit}
          onClick={handleDownload}
        >
          Download ZIP
        </Button>
      </HStack>
    </Box>
  );
};

export default SaktiBccImageDownloadForm;
